import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import OperatorLayout from "./OperatorLayout";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";

interface ProviderCostRow {
  provider_id: string;
  company_name: string;
  accepted_count: number;
  total_cents: number;
}

function getCurrentMonthKey() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

function useMonthLabel(t: (k: string) => string) {
  return (key: string) => {
    const [y, m] = key.split("-");
    const monthKeys = ["monthJan","monthFeb","monthMar","monthApr","monthMay","monthJun","monthJul","monthAug","monthSep","monthOct","monthNov","monthDec"];
    return `${t(`op.${monthKeys[Number(m) - 1]}`)} ${y}`;
  };
}

export default function OperatorCostOverview() {
  const { t } = useTranslation();
  const getMonthLabel = useMonthLabel(t);
  const [rows, setRows] = useState<ProviderCostRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [monthKey, setMonthKey] = useState(getCurrentMonthKey());
  const [availableMonths, setAvailableMonths] = useState<string[]>([]);

  useEffect(() => { loadAvailableMonths(); }, []);
  useEffect(() => { loadData(); }, [monthKey]);

  async function loadAvailableMonths() {
    const { data } = await supabase.from("opportunities").select("month_key").not("accepted_at", "is", null).not("month_key", "is", null);
    const keys = new Set<string>();
    keys.add(getCurrentMonthKey());
    if (data) { for (const row of data) { if (row.month_key) keys.add(row.month_key); } }
    setAvailableMonths(Array.from(keys).sort().reverse());
  }

  async function loadData() {
    setLoading(true);
    try {
      const { data: opps } = await supabase.from("opportunities").select("provider_id, price, status, accepted_at")
        .not("accepted_at", "is", null).in("status", ["in_review", "completed"])
        .gte("accepted_at", `${monthKey}-01T00:00:00Z`).lt("accepted_at", getNextMonthStart(monthKey));

      if (!opps || opps.length === 0) { setRows([]); setLoading(false); return; }

      const grouped: Record<string, { count: number; total: number }> = {};
      for (const opp of opps) {
        if (!grouped[opp.provider_id]) grouped[opp.provider_id] = { count: 0, total: 0 };
        grouped[opp.provider_id].count++;
        grouped[opp.provider_id].total += opp.price || 0;
      }

      const providerIds = Object.keys(grouped);
      const { data: providers } = await supabase.from("provider_profiles").select("id, company_name").in("id", providerIds);
      const nameMap: Record<string, string> = {};
      if (providers) { for (const p of providers) nameMap[p.id] = p.company_name || p.id.slice(0, 8); }

      const result: ProviderCostRow[] = providerIds.map((pid) => ({
        provider_id: pid, company_name: nameMap[pid] || pid.slice(0, 8) + "…",
        accepted_count: grouped[pid].count, total_cents: grouped[pid].total,
      })).sort((a, b) => b.total_cents - a.total_cents);

      setRows(result);
    } catch (err) { console.error("Cost overview error:", err); } finally { setLoading(false); }
  }

  const totalAll = rows.reduce((s, r) => s + r.total_cents, 0);
  const totalCount = rows.reduce((s, r) => s + r.accepted_count, 0);

  return (
    <OperatorLayout title={t("op.costTitle")} subtitle={t("op.costSubtitle")}>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex gap-2">
            {availableMonths.map((mk) => (
              <button key={mk} onClick={() => setMonthKey(mk)}
                className={cn("px-3 py-1.5 text-xs border transition-colors",
                  mk === monthKey ? "border-accent bg-accent/5 text-foreground" : "border-border text-muted-foreground hover:text-foreground")}>
                {getMonthLabel(mk)}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div className="p-6 border border-border bg-card">
            <span className="text-micro block mb-2">{t("op.costProviders")}</span>
            <p className="text-2xl font-medium text-foreground">{rows.length}</p>
          </div>
          <div className="p-6 border border-border bg-card">
            <span className="text-micro block mb-2">{t("op.costAccepted")}</span>
            <p className="text-2xl font-medium text-foreground">{totalCount}</p>
          </div>
          <div className="p-6 border border-border bg-card">
            <span className="text-micro block mb-2">{t("op.costTotal", { month: getMonthLabel(monthKey) })}</span>
            <p className="text-2xl font-medium price-display">{totalAll} €</p>
            <p className="text-micro mt-1">{t("op.costVatNote")}</p>
          </div>
        </div>

        <div className="border border-border bg-card overflow-x-auto">
          {loading ? (
            <p className="p-8 text-center text-caption">{t("common.loading")}</p>
          ) : rows.length === 0 ? (
            <p className="p-8 text-center text-caption">{t("op.costNoAccepted", { month: getMonthLabel(monthKey) })}</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left p-4 text-micro font-normal">{t("op.colProvider")}</th>
                  <th className="text-right p-4 text-micro font-normal">{t("op.colAccepted")}</th>
                  <th className="text-right p-4 text-micro font-normal">{t("op.colAmountNet")}</th>
                  <th className="text-right p-4 text-micro font-normal">{t("op.colAmountGross")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.provider_id} className="border-b border-border hover:bg-secondary/20 transition-colors">
                    <td className="p-4 font-medium">{row.company_name}</td>
                    <td className="p-4 text-right">{row.accepted_count}</td>
                    <td className="p-4 text-right">{row.total_cents} €</td>
                    <td className="p-4 text-right price-display font-medium">{(row.total_cents * 1.19).toFixed(2)} €</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-border bg-secondary/10">
                  <td className="p-4 font-medium">{t("op.costTotalRow")}</td>
                  <td className="p-4 text-right font-medium">{totalCount}</td>
                  <td className="p-4 text-right font-medium">{totalAll} €</td>
                  <td className="p-4 text-right price-display font-bold">{(totalAll * 1.19).toFixed(2)} €</td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>

        <p className="text-micro text-center">{t("op.costFooter")}</p>
      </div>
    </OperatorLayout>
  );
}

function getNextMonthStart(monthKey: string): string {
  const [y, m] = monthKey.split("-").map(Number);
  const nextMonth = m === 12 ? 1 : m + 1;
  const nextYear = m === 12 ? y + 1 : y;
  return `${nextYear}-${String(nextMonth).padStart(2, "0")}-01T00:00:00Z`;
}
